import {differenceInMinutes, format} from 'date-fns';
import invoke from 'lodash/invoke';
import uniqueId from 'lodash/uniqueId';
import {inject, observer} from 'mobx-react';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {graphql} from '@apollo/client/react/hoc/graphql';
import {injectIntl} from 'react-intl';

import {answerCampaignQuestionOptions} from 'api/campaign/mutation-options';
import {AnswerCampaignQuestion} from 'api/campaign/mutations';
import {FormattedMessage} from 'components/FormattedComponents';
import {FormButtonDesktop, FormButtonMobile} from 'components/FormButton';
import {DurationPicker} from 'components/DurationPicker';
import InfoModal from 'components/InfoModal';
import SliderRating from 'components/SliderRating';
import StarRating from 'components/StarRating';
import {CAMPAIGN_FOLLOW_UP_TYPE_ENUMS, CAMPAIGN_QUESTION_TYPE_ENUMS} from 'enums';
import {bindField} from 'utils/input-utils';

import {
  AnswerButton,
  AnswerButtons,
  CampaignQuestion,
  FooterSection,
  Footnote,
  Header,
  Heading,
  MultilineTextbox,
  ScrollableArea,
  Section,
  Summary,
  SummaryDetails,
  SummaryFootnote,
  SummaryItem,
  Textbox
} from './styles';

import messages from './messages';

const BINARY_ANSWER_TRUE = 1;
const BINARY_ANSWER_FALSE = 0;

const FormButtonComponent = ({device, ...props}) =>
  device === 'desktop' ? <FormButtonDesktop {...props} /> : <FormButtonMobile {...props} />;

@injectIntl
@graphql(AnswerCampaignQuestion, answerCampaignQuestionOptions)
@inject('store')
@observer
class CampaignQuestionComponent extends Component {
  static propTypes = {
    device: PropTypes.oneOf(['desktop', 'mobile']).isRequired,
    disableModal: PropTypes.bool,
    followUpQuestion: PropTypes.object,
    form: PropTypes.object,
    isSubmissionFailureModalOpen: PropTypes.bool,
    lastDateOfAnswerForUser: PropTypes.instanceOf(Date),
    onAnswer: PropTypes.func,
    onFollowUpAnswer: PropTypes.func,
    onSubmissionFailureModalClose: PropTypes.func,
    onSubmit: PropTypes.func,
    question: PropTypes.object,
    SliderComponent: PropTypes.elementType.isRequired,
    step: PropTypes.oneOf(['none', 'question', 'follow-up-question', 'summary', 'thank-you'])
  };

  userInputId = uniqueId('CampaignQuestion-user-');

  componentDidMount() {
    const {
      form,
      store: {
        auth: {user}
      }
    } = this.props;

    if (form && user) {
      form.$('user').sync(user.fullName);
    }
  }

  componentWillUnmount() {
    const {form} = this.props;

    if (form) {
      form.reset();
    }
  }

  submitAnswer = () => {
    const {form} = this.props;

    if (!form || !form.$('answer').isValid) {
      return;
    }

    invoke(this.props, 'onAnswer');
  };

  submitFollowUpAnswer = () => {
    invoke(this.props, 'onFollowUpAnswer');
  };

  submit = () => {
    const {answerCampaignQuestionMutation, instructionId, nextButton} = this.props;

    invoke(this.props, 'onSubmit', {
      instructionId,
      mutation: answerCampaignQuestionMutation,
      nextButton
    });
  };

  syncAnswer = (field, answer) => {
    const {form} = this.props;

    if (form) {
      form.$(field).sync(String(answer));
      form.validate();
    }
  };

  renderQuestion = () => {
    const {className, device, form, question, SliderComponent} = this.props;

    return (
      <CampaignQuestion className={className} device={device}>
        <ScrollableArea device={device}>
          <Section stretch>
            <Header device={device}>
              <FormattedMessage {...messages.campaign_sign_off} />
            </Header>
            <Heading data-cy="sign-off-question" device={device}>
              {question.question}
            </Heading>
            {question.kind === CAMPAIGN_QUESTION_TYPE_ENUMS.YES_NO && this.renderYesNoOptions()}
            {question.kind === CAMPAIGN_QUESTION_TYPE_ENUMS.PASS_FAIL && this.renderPassFailOptions()}
            {question.kind === CAMPAIGN_QUESTION_TYPE_ENUMS.TEXT && this.renderTextOptions()}
            {question.kind === CAMPAIGN_QUESTION_TYPE_ENUMS.STAR && (
              <StarRating
                numOfRating={question.numOfRating}
                onSelect={value => {
                  this.syncAnswer('answer', value);
                }}
              />
            )}
            {question.kind === CAMPAIGN_QUESTION_TYPE_ENUMS.NUMBER && (
              <SliderRating
                device={device}
                max={question.numOfRating}
                onChange={value => {
                  this.syncAnswer('answer', value);
                }}
                SliderComponent={SliderComponent}
                value={form && Number(form.$('answer').value)}
              />
            )}
          </Section>
          <Section>{this.renderFootnote()}</Section>
        </ScrollableArea>
        <FooterSection device={device}>
          <FormButtonComponent
            device={device}
            disabled={!form || !form.$('answer').isValid}
            onClick={this.submitAnswer}
            isInverse
          >
            <FormattedMessage {...messages.campaign_action_next} />
          </FormButtonComponent>
        </FooterSection>
      </CampaignQuestion>
    );
  };

  renderFollowUpQuestion = () => {
    const {
      className,
      device,
      followUpQuestion,
      form,
      intl: {formatMessage}
    } = this.props;

    return (
      <CampaignQuestion className={className} device={device}>
        <ScrollableArea device={device}>
          <Section stretch>
            <Header device={device}>
              <FormattedMessage {...messages.campaign_sign_off} />
            </Header>
            <Heading data-cy="sign-off-follow-up-question" device={device}>
              {followUpQuestion.question}
            </Heading>
            {followUpQuestion.kind === CAMPAIGN_FOLLOW_UP_TYPE_ENUMS.TEXT && (
              <MultilineTextbox
                placeholder={formatMessage(messages.campaign_type_answer)}
                {...(form && bindField(form, 'followUpAnswer'))}
              />
            )}
            {followUpQuestion.kind === CAMPAIGN_FOLLOW_UP_TYPE_ENUMS.TIME && (
              <DurationPicker
                device={device}
                onChange={value => {
                  this.syncAnswer('followUpAnswer', value);
                }}
              />
            )}
          </Section>
          <Section>{this.renderFootnote()}</Section>
        </ScrollableArea>
        <FooterSection device={device}>
          <FormButtonComponent device={device} onClick={this.submitFollowUpAnswer} isInverse>
            <FormattedMessage {...messages.campaign_action_next} />
          </FormButtonComponent>
        </FooterSection>
      </CampaignQuestion>
    );
  };

  renderSummary = () => {
    const {
      className,
      device,
      disableModal,
      form,
      intl: {formatMessage},
      isSubmissionFailureModalOpen,
      onSubmissionFailureModalClose,
      step
    } = this.props;

    /*
     * When the user has successfully answered all the questions and we have
     * moved him to the slide with the "Thank you" text, this thing will still
     * remain on the left. Don't forget to disable all the controls here so that
     * it's no longer interactive.
     */

    const isThankYouStep = step === 'thank-you';

    return (
      <CampaignQuestion className={className} device={device}>
        <ScrollableArea device={device}>
          <Section stretch>
            <Header device={device}>
              <FormattedMessage {...messages.campaign_sign_off} />
            </Header>
            <Heading device={device}>
              <FormattedMessage {...messages.campaign_submit_answers} />
            </Heading>
            <Summary>
              <SummaryItem>
                <label htmlFor={this.userInputId}>
                  <FormattedMessage {...messages.campaign_your_name} />
                </label>
              </SummaryItem>
              <SummaryDetails device={device}>
                <Textbox disabled={isThankYouStep} {...(form && bindField(form, 'user'))} id={this.userInputId} />
                <SummaryFootnote>
                  <FormattedMessage {...messages.campaign_by_submitting} />
                </SummaryFootnote>
              </SummaryDetails>
            </Summary>
          </Section>
          <Section>{this.renderFootnote()}</Section>
        </ScrollableArea>
        <FooterSection device={device}>
          <FormButtonComponent
            device={device}
            disabled={isThankYouStep || !form || !form.isValid}
            onClick={this.submit}
          >
            <FormattedMessage {...messages.campaign_action_submit} />
          </FormButtonComponent>
        </FooterSection>
        {!disableModal && (
          <InfoModal
            isDesktop={device === 'desktop'}
            isOpen={isSubmissionFailureModalOpen}
            onClose={onSubmissionFailureModalClose}
            onRightButtonClick={onSubmissionFailureModalClose}
            rightButtonText={formatMessage(messages.ok)}
            subtitle={formatMessage(messages.sign_off_submission_failure_subtitle)}
            title={formatMessage(messages.sign_off_submission_failure_title)}
          />
        )}
      </CampaignQuestion>
    );
  };

  renderFootnote = () => {
    const {device} = this.props;

    const timeAgoLabel = this.renderTimeAgoLabel();

    return timeAgoLabel ? (
      <Footnote device={device}>
        <FormattedMessage {...messages.campaign_last_sign_off} values={{timeAgoLabel}} />
      </Footnote>
    ) : null;
  };

  renderYesNoOptions = () =>
    this.renderOptions([
      {answer: BINARY_ANSWER_TRUE, label: <FormattedMessage {...messages.campaign_action_yes} />},
      {answer: BINARY_ANSWER_FALSE, label: <FormattedMessage {...messages.campaign_action_no} />}
    ]);

  renderPassFailOptions = () =>
    this.renderOptions([
      {answer: BINARY_ANSWER_TRUE, label: <FormattedMessage {...messages.campaign_action_pass} />},
      {answer: BINARY_ANSWER_FALSE, label: <FormattedMessage {...messages.campaign_action_fail} />}
    ]);

  renderTextOptions = () => {
    const {question} = this.props;

    return this.renderOptions(
      question.textTypeOptions.map((label, index) => {
        return {answer: index + 1, label};
      })
    );
  };

  renderOptions = answers => {
    const {device, form} = this.props;

    return (
      <AnswerButtons device={device}>
        {answers.map(({label, answer}) => {
          const normalizedValue = String(answer);

          return (
            <AnswerButton
              key={normalizedValue}
              device={device}
              isSelected={form && normalizedValue === form.$('answer').value}
              onClick={() => this.syncAnswer('answer', normalizedValue)}
            >
              {label}
            </AnswerButton>
          );
        })}
      </AnswerButtons>
    );
  };

  renderTimeAgoLabel = (dateNow = Date.now()) => {
    const {
      intl: {formatMessage},
      lastDateOfAnswerForUser
    } = this.props;

    if (lastDateOfAnswerForUser == null) {
      return null;
    }

    const minutesInHour = 60;
    const minutesInDay = 24 * minutesInHour;
    const minutesInMonth = 30 * minutesInDay;
    const minutesInYear = 12 * minutesInMonth;
    const diffInMinutes = differenceInMinutes(dateNow, new Date(lastDateOfAnswerForUser));

    const timestamp = format(lastDateOfAnswerForUser, 'M/D/YYYY h:mm A');

    if (diffInMinutes < minutesInHour) {
      return formatMessage(messages.campaign_minutes_ago, {
        minutes: diffInMinutes,
        timestamp
      });
    }

    if (diffInMinutes < minutesInDay) {
      return formatMessage(messages.campaign_hours_ago, {
        hours: Math.floor(diffInMinutes / minutesInHour),
        timestamp
      });
    }

    if (diffInMinutes < minutesInMonth) {
      return formatMessage(messages.campaign_days_ago, {
        days: Math.floor(diffInMinutes / minutesInDay),
        timestamp
      });
    }

    if (diffInMinutes < minutesInYear) {
      return formatMessage(messages.campaign_months_ago, {
        months: Math.floor(diffInMinutes / minutesInMonth),
        timestamp
      });
    }

    return formatMessage(messages.campaign_years_ago, {
      years: Math.floor(diffInMinutes / minutesInYear),
      timestamp
    });
  };

  render() {
    const {step} = this.props;

    if (step === 'question') {
      return this.renderQuestion();
    }

    if (step === 'follow-up-question') {
      return this.renderFollowUpQuestion();
    }

    if (step === 'summary' || step === 'thank-you') {
      return this.renderSummary();
    }

    return null;
  }
}

export default CampaignQuestionComponent;
